// PlayFirst Parent — "Your Bloom" card.
// A soft, ND-affirming alternative to streaks. Shows a 30-day rolling window
// of "leaves" (active days), a warm header line, and any newly-unlocked
// milestones as a one-time celebration toast.
//
// Endpoints used:
//   GET  /parent-app/bloom
//   PUT  /parent-app/bloom/opt-out
//   POST /parent-app/bloom/ack/{key}
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Sparkles, X, EyeOff } from 'lucide-react';
import { PFP } from './styles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Plant-themed warm headlines. We rotate by a stable hash of today's iso
// so the parent doesn't see whiplash refreshes within a day.
const HEADLINES = [
  (n, name) => `${n} day${n === 1 ? '' : 's'} of play with ${name} this month 💜`,
  (n, name) => `${n} small moment${n === 1 ? '' : 's'} with ${name} this month — that's a lot of love`,
  (n, name) => `${n} day${n === 1 ? '' : 's'} of showing up — every one of them counts`,
];

function pickHeadline(today, count, childName) {
  if (count === 0) return `A fresh page for you and ${childName} 💜`;
  const idx = (today || '').charCodeAt(8) % HEADLINES.length;
  return HEADLINES[idx](count, childName);
}

function MilestoneToast({ milestone, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: -8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -8 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-x-4 top-6 z-50 max-w-md mx-auto rounded-2xl bg-white shadow-2xl border border-[#7C6F9C]/25 p-5"
      data-testid={`bloom-milestone-toast-${milestone.key}`}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-3 right-3 text-stone-400 hover:text-stone-600"
        data-testid="bloom-milestone-close"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="text-3xl leading-none" aria-hidden>{milestone.emoji || '🌱'}</div>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider text-[#7C6F9C] font-medium">A small milestone</p>
          <h3 className="font-serif text-lg text-[#3a2f4f] mt-0.5">{milestone.title}</h3>
          <p className="text-sm text-stone-700 mt-1 leading-relaxed">{milestone.message}</p>
        </div>
      </div>
    </motion.div>
  );
}

export default function ParentBloomCard({ childFirstName = 'your child' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingCelebrations, setPendingCelebrations] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/parent-app/bloom`, auth());
        if (cancelled) return;
        setData(r.data);
        // Any milestones unlocked but not yet celebrated → queue one-time toast.
        const pending = (r.data.milestones || []).filter((m) => !m.celebrated_at);
        setPendingCelebrations(pending);
      } catch {
        // Silently swallow — Bloom should never block the home page.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismissCelebration = async (m) => {
    setPendingCelebrations((prev) => prev.filter((x) => x.key !== m.key));
    try {
      await axios.post(`${API}/parent-app/bloom/ack/${m.key}`, {}, auth());
    } catch { /* fire-and-forget */ }
  };

  const hide = async () => {
    if (!window.confirm("Hide the Bloom card? You can turn it back on in Settings.")) return;
    try {
      await axios.put(`${API}/parent-app/bloom/opt-out`, { opted_out: true }, auth());
      setData(null);
      toast.success('Bloom hidden — you can turn it back on in Settings.');
    } catch {
      toast.error("Couldn't update setting");
    }
  };

  const headline = useMemo(() => {
    if (!data) return '';
    return pickHeadline(data.today_iso, data.active_count, childFirstName);
  }, [data, childFirstName]);

  if (loading || !data || data.opted_out) return null;

  // 30 days into a 6-row × 5-col grid (rows = weeks-ish).
  const days = data.days || [];

  return (
    <>
      <AnimatePresence>
        {pendingCelebrations.map((m) => (
          <MilestoneToast
            key={m.key}
            milestone={m}
            onClose={() => dismissCelebration(m)}
          />
        ))}
      </AnimatePresence>

      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.08 }}
        className={`${PFP.card} p-5`}
        data-testid="bloom-card"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#7C6F9C]/10 text-[#7C6F9C] flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-[#7C6F9C] font-medium">Your bloom</p>
              <h3 className="font-serif text-lg text-[#3a2f4f] mt-0.5" data-testid="bloom-headline">
                {headline}
              </h3>
              {data.welcome_back && (
                <p className="text-sm text-stone-600 mt-1" data-testid="bloom-welcome-back">
                  Welcome back 💜 — no streak to chase, just glad you're here.
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={hide}
            className="text-stone-300 hover:text-stone-500"
            data-testid="bloom-hide-btn"
            title="Hide this card"
          >
            <EyeOff className="w-4 h-4" />
          </button>
        </div>

        {/* 30-day mini calendar — 6 rows × 5 cols */}
        <div
          className="grid grid-cols-10 gap-1.5 mt-2 mb-1"
          data-testid="bloom-calendar"
          aria-label="Your last 30 days of play"
        >
          {days.map((d) => (
            <div
              key={d.day_iso}
              title={`${d.day_iso}${d.active ? ' · active' : ''}`}
              data-testid={`bloom-day-${d.day_iso}`}
              className={
                'aspect-square rounded-md transition-all ' +
                (d.active
                  ? 'bg-gradient-to-br from-[#7C6F9C] to-[#a899c2] shadow-sm'
                  : 'bg-[#7C6F9C]/8 border border-[#7C6F9C]/10')
              }
            />
          ))}
        </div>

        {data.milestones && data.milestones.length > 0 && (
          <div className="mt-4 pt-3 border-t border-stone-100">
            <p className="text-xs uppercase tracking-wider text-stone-400 font-medium mb-2">
              Milestones
            </p>
            <div className="flex flex-wrap gap-2" data-testid="bloom-milestones">
              {data.milestones.map((m) => (
                <span
                  key={m.key}
                  title={m.message}
                  data-testid={`bloom-milestone-pill-${m.key}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#7C6F9C]/8 text-[#5b4f7a] text-xs"
                >
                  <span aria-hidden>{m.emoji || '🌱'}</span>
                  {m.title}
                </span>
              ))}
            </div>
          </div>
        )}
      </motion.section>
    </>
  );
}
